import { Chart, registerables } from 'chart.js';
import {
    fetchOverviewMetrics,
    fetchVisitorTimeSeries,
    fetchDeviceBreakdown,
    fetchGeoData,
    fetchReferrerSources,
    fetchFunnelData,
    fetchEngagementMetrics,
    fetchFormSubmissions,
    type FormSubmission,
} from './lib/dashboard-data';
import {
    fetchAllProperties,
    fetchPropertyBySlug,
    createProperty,
    updateProperty,
    deleteProperty,
    generateSlug,
    DEFAULT_RENDER_SETTINGS,
    type Property,
    type RenderSettings,
    type CustomButton,
} from './lib/properties';
import { fetchAllHDRs, createHDR, type HDRFile } from './lib/hdr-manager';
import { uploadHDRToCloudinary, listCloudinaryHDRs, type CloudinaryFile } from './lib/cloudinary';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

// Register Chart.js components
Chart.register(...registerables);

// Global chart theme
Chart.defaults.color = 'rgba(255, 255, 255, 0.5)';
Chart.defaults.borderColor = 'rgba(255, 255, 255, 0.06)';
Chart.defaults.font.family = 'Inter, sans-serif';
Chart.defaults.font.size = 11;

// -------------------------------------------------------------------
// Auth
// -------------------------------------------------------------------

const DASHBOARD_PASSWORD = 'letsgo';
const AUTH_KEY = 'villa_dashboard_auth';

function checkAuth(): boolean {
    return localStorage.getItem(AUTH_KEY) === 'true';
}

function initAuth(): void {
    const gate = document.getElementById('auth-gate')!;
    const dashboard = document.getElementById('dashboard')!;
    const form = document.getElementById('auth-form') as HTMLFormElement;
    const input = document.getElementById('auth-password') as HTMLInputElement;
    const error = document.getElementById('auth-error')!;

    if (checkAuth()) {
        gate.style.display = 'none';
        dashboard.style.display = 'flex';
        initDashboard();
        return;
    }

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (input.value === DASHBOARD_PASSWORD) {
            localStorage.setItem(AUTH_KEY, 'true');
            gate.style.display = 'none';
            dashboard.style.display = 'flex';
            initDashboard();
        } else {
            error.textContent = 'Incorrect password';
            input.value = '';
            input.focus();
        }
    });
}

// Logout
document.getElementById('logout-btn')?.addEventListener('click', () => {
    localStorage.removeItem(AUTH_KEY);
    window.location.reload();
});

// -------------------------------------------------------------------
// State
// -------------------------------------------------------------------

let currentDays = 30;
let currentPropertyId: string | null = null; // for analytics filtering
let isDemoMode = false; // true when viewing Diriyah demo analytics
let chartInstances: Record<string, Chart> = {};
let currentSubmissionsPage = 0;
let currentSearch = '';
let allProperties: Property[] = [];
let editingPropertyId: string | null = null;

// -------------------------------------------------------------------
// Diriyah Demo Data Generators
// -------------------------------------------------------------------

function generateDemoVisitorTimeSeries(days: number): { date: string; visitors: number }[] {
    const result: { date: string; visitors: number }[] = [];
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        // Realistic pattern: base ~30-60 visitors, weekend boost, slight growth trend
        const dayOfWeek = d.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const base = 32 + Math.floor((days - i) * 0.4); // slight uptrend
        const weekendBoost = isWeekend ? 15 : 0;
        const noise = Math.floor(Math.random() * 18) - 9;
        result.push({
            date: d.toISOString().split('T')[0],
            visitors: Math.max(8, base + weekendBoost + noise),
        });
    }
    return result;
}

function generateDemoOverviewMetrics() {
    return {
        uniqueVisitors: 1247,
        totalSessions: 1893,
        totalSubmissions: 34,
        avgDuration: 147, // 2m 27s
        conversionRate: 2.7,
    };
}

function generateDemoDeviceBreakdown() {
    return { desktop: 724, mobile: 412, tablet: 111 };
}

function generateDemoGeoData() {
    return [
        { country: 'Saudi Arabia', count: 487 },
        { country: 'United Arab Emirates', count: 219 },
        { country: 'United Kingdom', count: 168 },
        { country: 'United States', count: 132 },
        { country: 'France', count: 89 },
        { country: 'Germany', count: 67 },
        { country: 'Kuwait', count: 45 },
        { country: 'India', count: 40 },
    ];
}

function generateDemoReferrerSources() {
    return [
        { source: 'google.com', count: 412 },
        { source: 'direct', count: 318 },
        { source: 'instagram.com', count: 189 },
        { source: 'linkedin.com', count: 134 },
        { source: 'twitter.com', count: 87 },
        { source: 'facebook.com', count: 62 },
        { source: 'augmento.com', count: 45 },
    ];
}

function generateDemoFunnelData() {
    return {
        totalVisitors: 1247,
        engaged: 834,
        formViewers: 156,
        converted: 34,
    };
}

function generateDemoEngagementMetrics() {
    return {
        avgSessionDuration: 147,
        totalHotspotClicks: 3421,
        totalCameraMoves: 8932,
        totalApartmentsViewed: 2156,
        totalFormsOpened: 156,
        totalRoofToggles: 487,
        totalHdrChanges: 312,
    };
}

function generateDemoSubmissions(): { data: FormSubmission[]; total: number } {
    const names = [
        'Mohammed Al-Rashid', 'Sarah Thompson', 'Fatima Hassan', 'James Mitchell',
        'Noura Al-Qahtani', 'David Chen', 'Layla Ibrahim', 'Oliver Wright',
        'Aisha Ahmed', 'Thomas Müller', 'Reem Al-Dosari', 'Emma Johnson',
        'Khalid bin Saleh', 'Sophie Martin', 'Yusuf Al-Harbi', 'Isabella García',
        'Abdullah Al-Otaibi', 'Charlotte Brown', 'Hanan Al-Shehri', 'Lucas Dubois',
        'Maryam Al-Fahad', 'William Taylor', 'Nora Al-Sulaiman', 'Benjamin Lee',
        'Salma Hassan', 'Alexander Schmidt', 'Dina Khalil', 'Robert Anderson',
        'Lina Al-Mutairi', 'Henry Wilson', 'Amira Saeed', 'Jack Robinson',
        'Hala Al-Shamsi', 'George Harris',
    ];
    const emails = names.map((n) => n.toLowerCase().replace(/[^a-z]/g, '.').replace(/\.+/g, '.') + '@example.com');
    const countries = ['Saudi Arabia', 'United Kingdom', 'UAE', 'United States', 'France', 'Germany', 'Kuwait', 'India'];
    const devices = ['desktop', 'mobile', 'tablet'];
    const types = ['waitlist', 'apartment'];
    const budgets = ['$500K - $1M', '$1M - $2M', '$2M - $5M', '$5M+'];
    const months = ['March 2026', 'April 2026', 'May 2026', 'June 2026', 'July 2026'];

    const data: FormSubmission[] = names.map((name, i) => {
        const daysAgo = Math.floor(Math.random() * 28);
        const d = new Date();
        d.setDate(d.getDate() - daysAgo);
        return {
            id: `demo-${i}`,
            form_type: types[Math.floor(Math.random() * types.length)],
            name,
            email: emails[i],
            phone: Math.random() > 0.4 ? `+${Math.floor(Math.random() * 999)}-${Math.floor(Math.random() * 9000000 + 1000000)}` : null,
            preferred_month: months[Math.floor(Math.random() * months.length)],
            budget_range: budgets[Math.floor(Math.random() * budgets.length)],
            country: countries[Math.floor(Math.random() * countries.length)],
            device_type: devices[Math.floor(Math.random() * devices.length)],
            created_at: d.toISOString(),
            lead_score: Math.floor(Math.random() * 80) + 15,
        };
    }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return { data, total: data.length };
}

// -------------------------------------------------------------------
// Dashboard Init
// -------------------------------------------------------------------

function initDashboard(): void {
    setupSidebarNav();
    setupEditorEvents();
    routeFromURL();
}

// -------------------------------------------------------------------
// Routing
// -------------------------------------------------------------------

function routeFromURL(): void {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view') || 'overview';
    const slug = params.get('p');

    if (view === 'analytics') {
        if (slug === 'diriyah') {
            // Diriyah demo mode — use fake data, no DB lookup
            isDemoMode = true;
            currentPropertyId = 'demo-diriyah';
            showView('analytics');
        } else if (slug) {
            // Load specific property then show analytics
            isDemoMode = false;
            fetchPropertyBySlug(slug).then((prop) => {
                if (prop) {
                    currentPropertyId = prop.id;
                }
                showView('analytics');
            });
        } else {
            isDemoMode = false;
            currentPropertyId = null;
            showView('analytics');
        }
    } else if (view === 'edit') {
        if (slug) {
            fetchPropertyBySlug(slug).then((prop) => {
                if (prop) {
                    openEditor(prop);
                } else {
                    showView('overview');
                }
            });
        } else {
            // New property
            openEditor(null);
        }
    } else {
        showView('overview');
    }
}

function showView(view: 'overview' | 'analytics' | 'editor'): void {
    // Hide all views
    document.getElementById('view-overview')!.style.display = 'none';
    document.getElementById('view-analytics')!.style.display = 'none';
    document.getElementById('view-editor')!.style.display = 'none';

    // Update sidebar active state
    document.querySelectorAll('.sidebar-link[data-view]').forEach((el) => {
        el.classList.remove('active');
    });

    if (view === 'overview') {
        document.getElementById('view-overview')!.style.display = 'block';
        document.querySelector('.sidebar-link[data-view="overview"]')?.classList.add('active');
        loadOverviewPage();
    } else if (view === 'analytics') {
        document.getElementById('view-analytics')!.style.display = 'block';
        document.querySelector('.sidebar-link[data-view="analytics"]')?.classList.add('active');
        initAnalyticsView();
    } else if (view === 'editor') {
        document.getElementById('view-editor')!.style.display = 'block';
    }
}

function navigate(view: string, params?: Record<string, string>): void {
    const url = new URL(window.location.href);
    url.search = '';
    if (view !== 'overview') {
        url.searchParams.set('view', view);
    }
    if (params) {
        Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    }
    window.history.pushState({}, '', url.toString());
    routeFromURL();
}

// -------------------------------------------------------------------
// Sidebar Navigation
// -------------------------------------------------------------------

function setupSidebarNav(): void {
    document.querySelectorAll('.sidebar-link[data-view]').forEach((el) => {
        el.addEventListener('click', (e) => {
            e.preventDefault();
            const view = el.getAttribute('data-view') || 'overview';
            navigate(view);
        });
    });

    // Handle browser back/forward
    window.addEventListener('popstate', () => routeFromURL());
}

// -------------------------------------------------------------------
// Overview Page — Property Cards
// -------------------------------------------------------------------

async function loadOverviewPage(): Promise<void> {
    const grid = document.getElementById('property-grid')!;
    const emptyState = document.getElementById('empty-state')!;

    grid.innerHTML = '<div class="empty-state"><p>Loading properties...</p></div>';

    try {
        allProperties = await fetchAllProperties();

        if (allProperties.length === 0) {
            grid.innerHTML = '';
            emptyState.style.display = 'flex';
            return;
        }

        emptyState.style.display = 'none';

        // Fetch quick stats for each property in parallel
        const statsPromises = allProperties.map((p) =>
            fetchOverviewMetrics(30, p.id).catch(() => ({
                uniqueVisitors: 0,
                totalSessions: 0,
                totalSubmissions: 0,
                avgDuration: 0,
                conversionRate: 0,
            }))
        );

        const allStats = await Promise.all(statsPromises);

        const dbCards = allProperties.map((prop, i) => {
            const stats = allStats[i];
            const statusClass = prop.is_active ? 'status-active' : 'status-inactive';
            const statusLabel = prop.is_active ? 'Published' : 'Unpublished';
            const thumbnail = prop.thumbnail_url
                ? `<img src="${escapeHtml(prop.thumbnail_url)}" alt="${escapeHtml(prop.title)}">`
                : `<i class="hgi hgi-building-06 placeholder-icon"></i>`;

            return `<div class="property-card" data-id="${prop.id}">
                <div class="property-card-thumbnail">
                    ${thumbnail}
                    <span class="property-card-status ${statusClass}">${statusLabel}</span>
                </div>
                <div class="property-card-body">
                    <div class="property-card-title">${escapeHtml(prop.title)}</div>
                    <div class="property-card-subtitle">${escapeHtml(prop.subtitle || prop.slug)}</div>
                    <div class="property-card-stats">
                        <div class="property-card-stat">
                            <span class="property-card-stat-value">${stats.uniqueVisitors.toLocaleString()}</span>
                            <span class="property-card-stat-label">Visitors</span>
                        </div>
                        <div class="property-card-stat">
                            <span class="property-card-stat-value">${stats.totalSubmissions.toLocaleString()}</span>
                            <span class="property-card-stat-label">Submissions</span>
                        </div>
                        <div class="property-card-stat">
                            <span class="property-card-stat-value">${stats.conversionRate.toFixed(1)}%</span>
                            <span class="property-card-stat-label">Conversion</span>
                        </div>
                    </div>
                    <div class="property-card-actions">
                        <a href="/${prop.slug}" target="_blank" class="btn-secondary">
                            <i class="hgi hgi-eye"></i> View
                        </a>
                        <button class="btn-secondary" onclick="window.__editProperty('${prop.slug}')">
                            <i class="hgi hgi-edit-02"></i> Edit
                        </button>
                        <button class="btn-primary" onclick="window.__viewAnalytics('${prop.slug}')">
                            <i class="hgi hgi-chart-line-data-01"></i> Analytics
                        </button>
                    </div>
                </div>
            </div>`;
        }).join('');

        // Hardcoded Diriyah demo card (custom build, not in CMS)
        const diriyahCard = `<div class="property-card">
            <div class="property-card-thumbnail">
                <img src="https://res.cloudinary.com/dypt1fmsb/image/upload/v1771934859/gzkhmicouth0n9oqvv9h.png" alt="Diriyah">
                <span class="property-card-status status-active">Published</span>
            </div>
            <div class="property-card-body">
                <div class="property-card-title">Diriyah</div>
                <div class="property-card-subtitle">Heritage Village Experience</div>
                <div class="property-card-stats">
                    <div class="property-card-stat">
                        <span class="property-card-stat-value">1,247</span>
                        <span class="property-card-stat-label">Visitors</span>
                    </div>
                    <div class="property-card-stat">
                        <span class="property-card-stat-value">34</span>
                        <span class="property-card-stat-label">Submissions</span>
                    </div>
                    <div class="property-card-stat">
                        <span class="property-card-stat-value">2.7%</span>
                        <span class="property-card-stat-label">Conversion</span>
                    </div>
                </div>
                <div class="property-card-actions">
                    <a href="https://diriyahdemo.augmento.com" target="_blank" rel="noopener" class="btn-secondary">
                        <i class="hgi hgi-eye"></i> View
                    </a>
                    <button class="btn-secondary" disabled style="opacity:0.4;cursor:not-allowed">
                        <i class="hgi hgi-edit-02"></i> Edit
                    </button>
                    <button class="btn-primary" onclick="window.__viewAnalytics('diriyah')">
                        <i class="hgi hgi-chart-line-data-01"></i> Analytics
                    </button>
                </div>
            </div>
        </div>`;

        grid.innerHTML = dbCards + diriyahCard;
    } catch (err) {
        console.error('Failed to load properties:', err);
        grid.innerHTML = '<div class="empty-state"><p>Failed to load properties</p></div>';
    }
}

// Global handlers for onclick buttons in cards
(window as any).__editProperty = (slug: string) => navigate('edit', { p: slug });
(window as any).__viewAnalytics = (slug: string) => navigate('analytics', { p: slug });

// Add Property buttons
document.getElementById('add-property-btn')?.addEventListener('click', () => {
    navigate('edit');
});
document.getElementById('add-property-empty-btn')?.addEventListener('click', () => {
    navigate('edit');
});

// -------------------------------------------------------------------
// Analytics View
// -------------------------------------------------------------------

function initAnalyticsView(): void {
    populatePropertyFilter();
    setupTimeRangeTabs();
    setupSearch();
    setupExportCSV();
    loadAllAnalyticsData();
}

async function populatePropertyFilter(): Promise<void> {
    const select = document.getElementById('property-filter') as HTMLSelectElement;
    if (!select) return;

    // Keep current selection
    const currentVal = select.value;

    // Fetch properties if not loaded
    if (allProperties.length === 0) {
        allProperties = await fetchAllProperties();
    }

    // Build options
    select.innerHTML = '<option value="">All Properties</option>';
    allProperties.forEach((p) => {
        const option = document.createElement('option');
        option.value = p.id;
        option.textContent = p.title;
        select.appendChild(option);
    });

    // Add Diriyah demo option
    const diriyahOpt = document.createElement('option');
    diriyahOpt.value = 'demo-diriyah';
    diriyahOpt.textContent = 'Diriyah (Demo)';
    select.appendChild(diriyahOpt);

    // Restore selection
    if (currentPropertyId) {
        select.value = currentPropertyId;
    } else if (currentVal) {
        select.value = currentVal;
    }

    // Update subtitle
    updateAnalyticsSubtitle();

    // Listen for changes
    select.onchange = () => {
        const val = select.value;
        if (val === 'demo-diriyah') {
            isDemoMode = true;
            currentPropertyId = 'demo-diriyah';
        } else {
            isDemoMode = false;
            currentPropertyId = val || null;
        }
        updateAnalyticsSubtitle();
        currentSubmissionsPage = 0;
        loadAllAnalyticsData();
    };
}

function updateAnalyticsSubtitle(): void {
    const subtitle = document.getElementById('analytics-subtitle');
    if (!subtitle) return;

    if (isDemoMode) {
        subtitle.textContent = 'Diriyah — Heritage Village Experience';
    } else if (currentPropertyId) {
        const prop = allProperties.find((p) => p.id === currentPropertyId);
        subtitle.textContent = prop ? prop.title : 'Selected property';
    } else {
        subtitle.textContent = 'All properties';
    }
}

function setupTimeRangeTabs(): void {
    const tabs = document.querySelectorAll('#time-range-tabs .tab');
    tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
            tabs.forEach((t) => t.classList.remove('active'));
            tab.classList.add('active');
            currentDays = parseInt(tab.getAttribute('data-days') || '30');
            currentSubmissionsPage = 0;
            loadAllAnalyticsData();
        });
    });
}

function setupSearch(): void {
    const input = document.getElementById('submissions-search') as HTMLInputElement;
    let timeout: number;
    input?.addEventListener('input', () => {
        clearTimeout(timeout);
        timeout = window.setTimeout(() => {
            currentSearch = input.value;
            currentSubmissionsPage = 0;
            loadSubmissions();
        }, 300);
    });
}

function setupExportCSV(): void {
    document.getElementById('export-csv')?.addEventListener('click', async () => {
        const { data } = isDemoMode
            ? generateDemoSubmissions()
            : await fetchFormSubmissions(currentDays, '', 0, 10000, currentPropertyId);
        if (!data.length) return;

        const headers = ['Name', 'Email', 'Type', 'Phone', 'Country', 'Budget', 'Month', 'Device', 'Score', 'Date'];
        const rows = data.map((s: FormSubmission) => [
            s.name, s.email, s.form_type, s.phone || '', s.country || '',
            s.budget_range || '', s.preferred_month || '', s.device_type || '',
            String(s.lead_score || 0),
            new Date(s.created_at).toLocaleDateString(),
        ]);

        const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `submissions-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    });
}

// -------------------------------------------------------------------
// Load All Analytics Data
// -------------------------------------------------------------------

async function loadAllAnalyticsData(): Promise<void> {
    await Promise.all([
        loadOverviewCards(),
        loadVisitorChart(),
        loadDeviceChart(),
        loadGeoChart(),
        loadReferrerChart(),
        loadFunnelChart(),
        loadEngagementChart(),
        loadSubmissions(),
    ]);
}

// -------------------------------------------------------------------
// Overview Cards (Analytics)
// -------------------------------------------------------------------

async function loadOverviewCards(): Promise<void> {
    try {
        const m = isDemoMode
            ? generateDemoOverviewMetrics()
            : await fetchOverviewMetrics(currentDays, currentPropertyId);
        animateValue('val-visitors', m.uniqueVisitors);
        animateValue('val-submissions', m.totalSubmissions);
        animateValue('val-conversion', m.conversionRate, '%');

        const mins = Math.floor(m.avgDuration / 60);
        const secs = Math.round(m.avgDuration % 60);
        const el = document.getElementById('val-duration');
        if (el) el.textContent = `${mins}m ${secs}s`;
    } catch (err) {
        console.error('Failed to load overview:', err);
    }
}

function animateValue(id: string, target: number, suffix: string = ''): void {
    const el = document.getElementById(id);
    if (!el) return;

    const isFloat = suffix === '%';
    const duration = 800;
    const start = performance.now();

    function tick(now: number) {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = target * eased;

        if (isFloat) {
            el.textContent = current.toFixed(1) + suffix;
        } else {
            el.textContent = Math.round(current).toLocaleString() + suffix;
        }

        if (progress < 1) {
            requestAnimationFrame(tick);
        }
    }

    requestAnimationFrame(tick);
}

// -------------------------------------------------------------------
// Charts
// -------------------------------------------------------------------

async function loadVisitorChart(): Promise<void> {
    try {
        const data = isDemoMode
            ? generateDemoVisitorTimeSeries(currentDays)
            : await fetchVisitorTimeSeries(currentDays, currentPropertyId);

        if (chartInstances['visitor']) chartInstances['visitor'].destroy();

        const ctx = (document.getElementById('visitor-chart') as HTMLCanvasElement)?.getContext('2d');
        if (!ctx) return;

        const gradient = ctx.createLinearGradient(0, 0, 0, 250);
        gradient.addColorStop(0, 'rgba(0, 180, 216, 0.15)');
        gradient.addColorStop(1, 'rgba(0, 180, 216, 0)');

        chartInstances['visitor'] = new Chart(ctx, {
            type: 'line',
            data: {
                labels: data.map((p) => {
                    const d = new Date(p.date);
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }),
                datasets: [{
                    label: 'Visitors',
                    data: data.map((p) => p.visitors),
                    borderColor: '#00B4D8',
                    backgroundColor: gradient,
                    fill: true,
                    tension: 0.4,
                    pointRadius: data.length > 30 ? 0 : 3,
                    pointBackgroundColor: '#00B4D8',
                    pointHoverRadius: 5,
                    borderWidth: 2,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { intersect: false, mode: 'index' },
                plugins: { legend: { display: false } },
                scales: {
                    x: {
                        grid: { color: 'rgba(255,255,255,0.03)' },
                        ticks: { maxTicksLimit: 10, font: { size: 10 } },
                    },
                    y: {
                        grid: { color: 'rgba(255,255,255,0.03)' },
                        beginAtZero: true,
                        ticks: { font: { size: 10 } },
                    },
                },
            },
        });
    } catch (err) {
        console.error('Failed to load visitor chart:', err);
    }
}

async function loadDeviceChart(): Promise<void> {
    try {
        const data = isDemoMode
            ? generateDemoDeviceBreakdown()
            : await fetchDeviceBreakdown(currentDays, currentPropertyId);

        if (chartInstances['device']) chartInstances['device'].destroy();

        const ctx = (document.getElementById('device-chart') as HTMLCanvasElement)?.getContext('2d');
        if (!ctx) return;

        const total = data.desktop + data.mobile + data.tablet;

        chartInstances['device'] = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: ['Desktop', 'Mobile', 'Tablet'],
                datasets: [{
                    data: [data.desktop, data.mobile, data.tablet],
                    backgroundColor: ['#00B4D8', '#8b5cf6', '#10b981'],
                    borderWidth: 0,
                    hoverOffset: 4,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '70%',
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: { padding: 16, usePointStyle: true, pointStyleWidth: 8, font: { size: 11 } },
                    },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                const val = ctx.raw as number;
                                const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
                                return `${ctx.label}: ${val} (${pct}%)`;
                            },
                        },
                    },
                },
            },
        });
    } catch (err) {
        console.error('Failed to load device chart:', err);
    }
}

async function loadGeoChart(): Promise<void> {
    try {
        const data = isDemoMode
            ? generateDemoGeoData()
            : await fetchGeoData(currentDays, currentPropertyId);

        if (chartInstances['geo']) chartInstances['geo'].destroy();

        const ctx = (document.getElementById('geo-chart') as HTMLCanvasElement)?.getContext('2d');
        if (!ctx) return;

        if (data.length === 0) {
            showEmpty('geo-chart', 'No geographic data yet');
            return;
        }

        chartInstances['geo'] = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: data.map((g) => g.country),
                datasets: [{
                    data: data.map((g) => g.count),
                    backgroundColor: 'rgba(0, 180, 216, 0.4)',
                    borderColor: '#00B4D8',
                    borderWidth: 1,
                    borderRadius: 4,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                plugins: { legend: { display: false } },
                scales: {
                    x: {
                        grid: { color: 'rgba(255,255,255,0.03)' },
                        beginAtZero: true,
                        ticks: { font: { size: 10 } },
                    },
                    y: {
                        grid: { display: false },
                        ticks: { font: { size: 11 } },
                    },
                },
            },
        });
    } catch (err) {
        console.error('Failed to load geo chart:', err);
    }
}

async function loadReferrerChart(): Promise<void> {
    try {
        const data = isDemoMode
            ? generateDemoReferrerSources()
            : await fetchReferrerSources(currentDays, currentPropertyId);

        if (chartInstances['referrer']) chartInstances['referrer'].destroy();

        const ctx = (document.getElementById('referrer-chart') as HTMLCanvasElement)?.getContext('2d');
        if (!ctx) return;

        if (data.length === 0) {
            showEmpty('referrer-chart', 'No traffic source data yet');
            return;
        }

        chartInstances['referrer'] = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: data.map((r) => r.source),
                datasets: [{
                    data: data.map((r) => r.count),
                    backgroundColor: 'rgba(139, 92, 246, 0.4)',
                    borderColor: '#8b5cf6',
                    borderWidth: 1,
                    borderRadius: 4,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                plugins: { legend: { display: false } },
                scales: {
                    x: {
                        grid: { color: 'rgba(255,255,255,0.03)' },
                        beginAtZero: true,
                        ticks: { font: { size: 10 } },
                    },
                    y: {
                        grid: { display: false },
                        ticks: { font: { size: 11 } },
                    },
                },
            },
        });
    } catch (err) {
        console.error('Failed to load referrer chart:', err);
    }
}

async function loadFunnelChart(): Promise<void> {
    try {
        const data = isDemoMode
            ? generateDemoFunnelData()
            : await fetchFunnelData(currentDays, currentPropertyId);

        if (chartInstances['funnel']) chartInstances['funnel'].destroy();

        const ctx = (document.getElementById('funnel-chart') as HTMLCanvasElement)?.getContext('2d');
        if (!ctx) return;

        const labels = ['All Visitors', 'Engaged (>30s)', 'Opened Form', 'Submitted'];
        const values = [data.totalVisitors, data.engaged, data.formViewers, data.converted];

        chartInstances['funnel'] = new Chart(ctx, {
            type: 'bar',
            data: {
                labels,
                datasets: [{
                    data: values,
                    backgroundColor: [
                        'rgba(0, 180, 216, 0.5)',
                        'rgba(0, 180, 216, 0.4)',
                        'rgba(245, 158, 11, 0.4)',
                        'rgba(16, 185, 129, 0.5)',
                    ],
                    borderColor: ['#00B4D8', '#00B4D8', '#f59e0b', '#10b981'],
                    borderWidth: 1,
                    borderRadius: 6,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                const val = ctx.raw as number;
                                const pct = data.totalVisitors > 0 ? ((val / data.totalVisitors) * 100).toFixed(1) : '0';
                                return `${val} (${pct}%)`;
                            },
                        },
                    },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                    y: {
                        grid: { color: 'rgba(255,255,255,0.03)' },
                        beginAtZero: true,
                        ticks: { font: { size: 10 } },
                    },
                },
            },
        });
    } catch (err) {
        console.error('Failed to load funnel chart:', err);
    }
}

async function loadEngagementChart(): Promise<void> {
    try {
        const data = isDemoMode
            ? generateDemoEngagementMetrics()
            : await fetchEngagementMetrics(currentDays, currentPropertyId);

        if (chartInstances['engagement']) chartInstances['engagement'].destroy();

        const ctx = (document.getElementById('engagement-chart') as HTMLCanvasElement)?.getContext('2d');
        if (!ctx) return;

        const labels = ['Hotspot Clicks', 'Camera Moves', 'Apts Viewed', 'Forms Opened', 'Roof Toggles', 'HDR Changes'];
        const values = [
            data.totalHotspotClicks, data.totalCameraMoves, data.totalApartmentsViewed,
            data.totalFormsOpened, data.totalRoofToggles, data.totalHdrChanges,
        ];

        chartInstances['engagement'] = new Chart(ctx, {
            type: 'bar',
            data: {
                labels,
                datasets: [{
                    data: values,
                    backgroundColor: [
                        'rgba(0, 180, 216, 0.5)', 'rgba(139, 92, 246, 0.5)', 'rgba(16, 185, 129, 0.5)',
                        'rgba(245, 158, 11, 0.5)', 'rgba(236, 72, 153, 0.5)', 'rgba(99, 102, 241, 0.5)',
                    ],
                    borderWidth: 0,
                    borderRadius: 6,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                    y: {
                        grid: { color: 'rgba(255,255,255,0.03)' },
                        beginAtZero: true,
                        ticks: { font: { size: 10 } },
                    },
                },
            },
        });
    } catch (err) {
        console.error('Failed to load engagement chart:', err);
    }
}

// -------------------------------------------------------------------
// Form Submissions Table
// -------------------------------------------------------------------

async function loadSubmissions(): Promise<void> {
    try {
        let data: FormSubmission[];
        let total: number;

        if (isDemoMode) {
            const demo = generateDemoSubmissions();
            // Apply search filter on demo data
            const filtered = currentSearch
                ? demo.data.filter((s) => s.name.toLowerCase().includes(currentSearch.toLowerCase()) || s.email.toLowerCase().includes(currentSearch.toLowerCase()))
                : demo.data;
            total = filtered.length;
            data = filtered.slice(currentSubmissionsPage * 20, (currentSubmissionsPage + 1) * 20);
        } else {
            const result = await fetchFormSubmissions(currentDays, currentSearch, currentSubmissionsPage, 20, currentPropertyId);
            data = result.data;
            total = result.total;
        }

        const tbody = document.getElementById('submissions-tbody');
        if (!tbody) return;

        if (data.length === 0) {
            tbody.innerHTML = `<tr><td colspan="7" class="empty-state"><i class="hgi hgi-inbox-01"></i><span>No submissions yet</span></td></tr>`;
            renderPagination(0);
            return;
        }

        tbody.innerHTML = data.map((s: FormSubmission) => {
            const typeClass = s.form_type === 'waitlist' ? 'type-waitlist' : 'type-apartment';
            const typeLabel = s.form_type === 'waitlist' ? 'Waitlist' : 'Apartment';
            const score = s.lead_score || 0;
            const scoreClass = score >= 60 ? 'score-high' : score >= 35 ? 'score-medium' : 'score-low';
            const date = new Date(s.created_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
            });

            return `<tr>
                <td style="color:var(--text);font-weight:500">${escapeHtml(s.name)}</td>
                <td>${escapeHtml(s.email)}</td>
                <td><span class="type-badge ${typeClass}">${typeLabel}</span></td>
                <td>${escapeHtml(s.country || '—')}</td>
                <td>${escapeHtml(s.device_type || '—')}</td>
                <td><span class="score-badge ${scoreClass}">${score}</span></td>
                <td>${date}</td>
            </tr>`;
        }).join('');

        renderPagination(total);
    } catch (err) {
        console.error('Failed to load submissions:', err);
    }
}

function renderPagination(total: number): void {
    const container = document.getElementById('submissions-pagination');
    if (!container) return;

    const pageSize = 20;
    const pages = Math.ceil(total / pageSize);

    if (pages <= 1) {
        container.innerHTML = '';
        return;
    }

    let html = '';
    for (let i = 0; i < pages; i++) {
        html += `<button class="page-btn ${i === currentSubmissionsPage ? 'active' : ''}" data-page="${i}">${i + 1}</button>`;
    }
    container.innerHTML = html;

    container.querySelectorAll('.page-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            currentSubmissionsPage = parseInt(btn.getAttribute('data-page') || '0');
            loadSubmissions();
        });
    });
}

// -------------------------------------------------------------------
// Property Editor
// -------------------------------------------------------------------

const RENDER_SETTING_KEYS: (keyof RenderSettings)[] = [
    'exposure', 'toneMapping', 'ambientIntensity', 'ambientColor',
    'directIntensity', 'directColor', 'envIntensity', 'ssaoIntensity',
    'ssaoRadius', 'vignetteOffset', 'vignetteDarkness', 'bloomIntensity',
    'bloomLuminanceThreshold', 'fogDensity', 'modelScale', 'hideHdrBackground',
    'showSunSlider',
];

// -------------------------------------------------------------------
// HDR Dropdown + Upload
// -------------------------------------------------------------------

let hdrList: HDRFile[] = [];

async function populateHDRDropdown(selectedName?: string): Promise<void> {
    const select = document.getElementById('prop-hdr') as HTMLSelectElement;
    if (!select) return;

    if (hdrList.length === 0) {
        hdrList = await fetchAllHDRs();
    }

    // Only show HDRs that are checked in the checklist (or all if none checked)
    const checkedNames = getCheckedHDRNames();
    const filteredList = checkedNames.length > 0
        ? hdrList.filter((h) => checkedNames.includes(h.name))
        : hdrList;

    select.innerHTML = '';
    filteredList.forEach((hdr) => {
        const opt = document.createElement('option');
        opt.value = hdr.name;
        opt.textContent = hdr.name;
        select.appendChild(opt);
    });

    // Select the matching option
    if (selectedName && filteredList.some((h) => h.name === selectedName)) {
        select.value = selectedName;
    }
}

function getCheckedHDRNames(): string[] {
    const checkboxes = document.querySelectorAll('#hdr-checklist input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
    return Array.from(checkboxes).map((cb) => cb.value);
}

function populateHDRChecklist(activeNames: string[]): void {
    const container = document.getElementById('hdr-checklist');
    if (!container) return;

    container.innerHTML = '';

    hdrList.forEach((hdr) => {
        const isChecked = activeNames.length === 0 || activeNames.includes(hdr.name);
        const label = document.createElement('label');
        label.className = 'hdr-check-item';
        label.innerHTML = `
            <input type="checkbox" value="${escapeHtml(hdr.name)}" ${isChecked ? 'checked' : ''}>
            <span class="hdr-check-name">${escapeHtml(hdr.name)}</span>
        `;
        container.appendChild(label);
    });

    // When checkboxes change, update the default HDR dropdown
    container.addEventListener('change', () => {
        const currentDefault = (document.getElementById('prop-hdr') as HTMLSelectElement).value;
        populateHDRDropdown(currentDefault);
    });
}

function setupHDRUpload(): void {
    const fileInput = document.getElementById('hdr-file-input') as HTMLInputElement;
    if (!fileInput) return;

    fileInput.addEventListener('change', async () => {
        const file = fileInput.files?.[0];
        if (!file) return;

        const progressContainer = document.getElementById('hdr-upload-progress')!;
        const progressBar = document.getElementById('hdr-upload-bar')!;
        const progressText = document.getElementById('hdr-upload-text')!;
        const uploadBtn = document.getElementById('hdr-upload-btn')!;

        // Show progress, disable button
        progressContainer.style.display = 'flex';
        uploadBtn.classList.add('disabled');
        progressBar.style.width = '0%';
        progressText.textContent = 'Uploading to Cloudinary...';

        try {
            // 1. Upload to Cloudinary
            const result = await uploadHDRToCloudinary(file, (percent) => {
                progressBar.style.width = `${percent}%`;
                progressText.textContent = `Uploading... ${percent}%`;
            });

            progressText.textContent = 'Saving to database...';
            progressBar.style.width = '100%';

            // 2. Create DB record — use filename without extension as name
            const hdrName = file.name.replace(/\.(hdr|exr)$/i, '').replace(/[_-]/g, ' ');
            const newHDR = await createHDR({
                name: hdrName,
                url: result.secure_url,
                cloudinary_public_id: result.public_id,
                sun_time: 12.0,
            });

            // 3. Refresh dropdown and select the new one
            await populateHDRDropdown(newHDR?.name || hdrName);

            showToast(`HDR "${hdrName}" uploaded successfully`);
        } catch (err: any) {
            console.error('[HDR Upload] Failed:', err);
            showToast(err.message || 'HDR upload failed', true);
        } finally {
            progressContainer.style.display = 'none';
            uploadBtn.classList.remove('disabled');
            // Reset file input so same file can be re-selected
            fileInput.value = '';
        }
    });
}

// -------------------------------------------------------------------
// Browse Cloudinary HDRs
// -------------------------------------------------------------------

let selectedCloudinaryFile: CloudinaryFile | null = null;

function setupCloudinaryBrowse(): void {
    const browseBtn = document.getElementById('hdr-browse-btn');
    if (!browseBtn) return;

    browseBtn.addEventListener('click', () => openCloudinaryBrowseModal());

    document.getElementById('cloudinary-modal-close')?.addEventListener('click', closeCloudinaryBrowseModal);
    document.getElementById('cloudinary-cancel-btn')?.addEventListener('click', closeCloudinaryBrowseModal);

    document.getElementById('cloudinary-browse-modal')?.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).id === 'cloudinary-browse-modal') {
            closeCloudinaryBrowseModal();
        }
    });

    document.getElementById('cloudinary-select-btn')?.addEventListener('click', () => onCloudinaryFileSelected());
}

async function openCloudinaryBrowseModal(): Promise<void> {
    const modal = document.getElementById('cloudinary-browse-modal')!;
    const loading = document.getElementById('cloudinary-files-loading')!;
    const grid = document.getElementById('cloudinary-files-grid')!;
    const footer = document.getElementById('cloudinary-modal-footer')!;
    const selectBtn = document.getElementById('cloudinary-select-btn') as HTMLButtonElement;

    selectedCloudinaryFile = null;
    selectBtn.disabled = true;
    grid.innerHTML = '';
    grid.style.display = 'none';
    footer.style.display = 'none';
    loading.style.display = 'flex';
    loading.innerHTML = '<i class="hgi hgi-loading-01 spin"></i> Loading files from Cloudinary...';

    modal.style.display = 'flex';

    try {
        const files = await listCloudinaryHDRs();

        if (files.length === 0) {
            loading.innerHTML = '<span>No HDR files found in Cloudinary <code>hdr</code> folder. Upload one first!</span>';
            footer.style.display = 'flex';
            return;
        }

        const existingIds = new Set(hdrList.map(h => h.cloudinary_public_id));

        grid.innerHTML = files.map(file => {
            const isInDB = existingIds.has(file.public_id);
            const sizeKB = (file.bytes / 1024).toFixed(0);
            const sizeMB = (file.bytes / (1024 * 1024)).toFixed(1);
            const sizeLabel = file.bytes > 1024 * 1024 ? `${sizeMB} MB` : `${sizeKB} KB`;

            return `<div class="cloudinary-file-card" data-public-id="${escapeHtml(file.public_id)}" data-secure-url="${escapeHtml(file.secure_url)}" data-display-name="${escapeHtml(file.display_name)}">
                <div class="cloudinary-file-icon"><i class="hgi hgi-sun-03"></i></div>
                <div class="cloudinary-file-info">
                    <span class="cloudinary-file-name">${escapeHtml(file.display_name)}</span>
                    <span class="cloudinary-file-meta">${sizeLabel}${isInDB ? ' &middot; <span class="cloudinary-in-db">In library</span>' : ''}</span>
                </div>
                <div class="cloudinary-file-check"><i class="hgi hgi-checkmark-circle-02"></i></div>
            </div>`;
        }).join('');

        loading.style.display = 'none';
        grid.style.display = 'grid';
        footer.style.display = 'flex';

        grid.querySelectorAll('.cloudinary-file-card').forEach(card => {
            card.addEventListener('click', () => {
                grid.querySelectorAll('.cloudinary-file-card').forEach(c => c.classList.remove('selected'));
                card.classList.add('selected');
                selectedCloudinaryFile = {
                    public_id: (card as HTMLElement).dataset.publicId!,
                    secure_url: (card as HTMLElement).dataset.secureUrl!,
                    display_name: (card as HTMLElement).dataset.displayName!,
                    format: '',
                    bytes: 0,
                    created_at: '',
                };
                selectBtn.disabled = false;
            });
        });

    } catch (err: any) {
        console.error('[Cloudinary Browse] Failed:', err);
        loading.innerHTML = `<span style="color:var(--danger)">Failed to load: ${escapeHtml(err.message)}</span>`;
        footer.style.display = 'flex';
    }
}

function closeCloudinaryBrowseModal(): void {
    document.getElementById('cloudinary-browse-modal')!.style.display = 'none';
    selectedCloudinaryFile = null;
}

async function onCloudinaryFileSelected(): Promise<void> {
    if (!selectedCloudinaryFile) return;

    const selectBtn = document.getElementById('cloudinary-select-btn') as HTMLButtonElement;
    selectBtn.innerHTML = '<i class="hgi hgi-loading-01 spin"></i> Adding...';
    selectBtn.disabled = true;

    try {
        const existing = hdrList.find(h => h.cloudinary_public_id === selectedCloudinaryFile!.public_id);

        if (existing) {
            await populateHDRDropdown(existing.name);
            showToast(`Selected existing HDR "${existing.name}"`);
        } else {
            const newHDR = await createHDR({
                name: selectedCloudinaryFile.display_name,
                url: selectedCloudinaryFile.secure_url,
                cloudinary_public_id: selectedCloudinaryFile.public_id,
                sun_time: 12.0,
            });
            await populateHDRDropdown(newHDR?.name || selectedCloudinaryFile.display_name);
            showToast(`Added HDR "${selectedCloudinaryFile.display_name}" to library`);
        }

        closeCloudinaryBrowseModal();
    } catch (err: any) {
        console.error('[Cloudinary Browse] Select failed:', err);
        showToast(err.message || 'Failed to add HDR from Cloudinary', true);
        selectBtn.innerHTML = '<i class="hgi hgi-checkmark-circle-02"></i> Use Selected HDR';
        selectBtn.disabled = false;
    }
}

// -------------------------------------------------------------------
// Icon Picker
// -------------------------------------------------------------------

const ICON_CHOICES: { name: string; label: string }[] = [
    // Architecture & Building
    { name: 'hgi-home-04', label: 'Home' },
    { name: 'hgi-building-06', label: 'Building' },
    { name: 'hgi-roof-02', label: 'Roof' },
    { name: 'hgi-door-01', label: 'Door' },
    { name: 'hgi-house-04', label: 'House' },
    { name: 'hgi-office-01', label: 'Office' },
    { name: 'hgi-city-02', label: 'City' },
    { name: 'hgi-structure-05', label: 'Structure' },
    // Interior & Furniture
    { name: 'hgi-sofa-01', label: 'Sofa' },
    { name: 'hgi-chair-01', label: 'Chair' },
    { name: 'hgi-bed-single-01', label: 'Bed' },
    { name: 'hgi-bathtub-01', label: 'Bathtub' },
    { name: 'hgi-kitchen-utensils', label: 'Kitchen' },
    { name: 'hgi-tv-01', label: 'TV' },
    { name: 'hgi-lamp-01', label: 'Lamp' },
    { name: 'hgi-table-lamp-01', label: 'Table Lamp' },
    // Visibility & View
    { name: 'hgi-eye', label: 'Eye' },
    { name: 'hgi-view', label: 'View' },
    { name: 'hgi-invisible', label: 'Hidden' },
    { name: 'hgi-layers-01', label: 'Layers' },
    { name: 'hgi-flip-top', label: 'Flip' },
    { name: 'hgi-grid', label: 'Grid' },
    { name: 'hgi-layout-grid', label: 'Layout' },
    // 3D & Shapes
    { name: 'hgi-cube-01', label: 'Cube' },
    { name: 'hgi-sphere', label: 'Sphere' },
    { name: 'hgi-cylinder-01', label: 'Cylinder' },
    { name: 'hgi-rotate-360', label: 'Rotate 360' },
    { name: 'hgi-3d-rotate', label: '3D Rotate' },
    { name: 'hgi-3d-view', label: '3D View' },
    // Nature & Environment
    { name: 'hgi-sun-03', label: 'Sun' },
    { name: 'hgi-moon-02', label: 'Moon' },
    { name: 'hgi-cloud', label: 'Cloud' },
    { name: 'hgi-rain', label: 'Rain' },
    { name: 'hgi-tree-06', label: 'Tree' },
    { name: 'hgi-leaf-01', label: 'Leaf' },
    { name: 'hgi-flower', label: 'Flower' },
    { name: 'hgi-mountain', label: 'Mountain' },
    { name: 'hgi-ocean', label: 'Ocean' },
    { name: 'hgi-fire', label: 'Fire' },
    // Navigation & Arrows
    { name: 'hgi-navigation-03', label: 'Navigate' },
    { name: 'hgi-arrow-up-01', label: 'Up' },
    { name: 'hgi-arrow-down-01', label: 'Down' },
    { name: 'hgi-arrow-right-02', label: 'Right' },
    { name: 'hgi-arrow-left-02', label: 'Left' },
    { name: 'hgi-compass', label: 'Compass' },
    { name: 'hgi-location-01', label: 'Location' },
    // Actions & Tools
    { name: 'hgi-paint-board', label: 'Paint' },
    { name: 'hgi-color-swatch', label: 'Color' },
    { name: 'hgi-setting-07', label: 'Settings' },
    { name: 'hgi-tools', label: 'Tools' },
    { name: 'hgi-magic-wand-01', label: 'Magic' },
    { name: 'hgi-flash', label: 'Flash' },
    { name: 'hgi-bulb', label: 'Light Bulb' },
    // Media
    { name: 'hgi-camera-01', label: 'Camera' },
    { name: 'hgi-image-01', label: 'Image' },
    { name: 'hgi-video-01', label: 'Video' },
    { name: 'hgi-play-circle', label: 'Play' },
    // Objects
    { name: 'hgi-car-01', label: 'Car' },
    { name: 'hgi-swimming', label: 'Pool' },
    { name: 'hgi-umbrella', label: 'Umbrella' },
    { name: 'hgi-fence', label: 'Fence' },
    { name: 'hgi-solar-energy', label: 'Solar' },
    { name: 'hgi-wind-power', label: 'Wind' },
    // Status & Indicators
    { name: 'hgi-checkmark-circle-02', label: 'Check' },
    { name: 'hgi-information-circle', label: 'Info' },
    { name: 'hgi-alert-02', label: 'Alert' },
    { name: 'hgi-star', label: 'Star' },
    { name: 'hgi-favourite', label: 'Heart' },
    { name: 'hgi-sparkle', label: 'Sparkle' },
    // Misc
    { name: 'hgi-touch-interaction-02', label: 'Touch' },
    { name: 'hgi-cursor-02', label: 'Cursor' },
    { name: 'hgi-maximize-01', label: 'Maximize' },
    { name: 'hgi-minimize-01', label: 'Minimize' },
    { name: 'hgi-expand-01', label: 'Expand' },
];

let iconPickerTargetIdx = -1;

function setupIconPicker(): void {
    // Close on overlay click
    document.getElementById('icon-picker-modal')?.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).id === 'icon-picker-modal') {
            closeIconPicker();
        }
    });

    // Search filter
    document.getElementById('icon-picker-search')?.addEventListener('input', (e) => {
        const query = (e.target as HTMLInputElement).value.toLowerCase();
        renderIconGrid(query);
    });

    // Custom icon input
    document.getElementById('icon-picker-custom-btn')?.addEventListener('click', () => {
        const input = document.getElementById('icon-picker-custom') as HTMLInputElement;
        const val = input.value.trim();
        if (val) {
            selectIcon(val.startsWith('hgi-') ? val : `hgi-${val}`);
        }
    });

    document.getElementById('icon-picker-custom')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const input = e.target as HTMLInputElement;
            const val = input.value.trim();
            if (val) {
                selectIcon(val.startsWith('hgi-') ? val : `hgi-${val}`);
            }
        }
    });
}

function openIconPicker(buttonIndex: number): void {
    iconPickerTargetIdx = buttonIndex;
    const modal = document.getElementById('icon-picker-modal')!;
    const searchInput = document.getElementById('icon-picker-search') as HTMLInputElement;
    const customInput = document.getElementById('icon-picker-custom') as HTMLInputElement;

    searchInput.value = '';
    customInput.value = customButtons[buttonIndex]?.icon || 'hgi-eye';

    renderIconGrid();
    modal.style.display = 'flex';
    searchInput.focus();
}

function renderIconGrid(filter?: string): void {
    const grid = document.getElementById('icon-picker-grid')!;
    const currentIcon = customButtons[iconPickerTargetIdx]?.icon || 'hgi-eye';

    const filtered = filter
        ? ICON_CHOICES.filter(ic => ic.name.includes(filter) || ic.label.toLowerCase().includes(filter))
        : ICON_CHOICES;

    grid.innerHTML = filtered.map(ic => {
        const isSelected = ic.name === currentIcon;
        return `<div class="icon-picker-item${isSelected ? ' selected' : ''}" data-icon="${ic.name}" title="${ic.label}">
            <i class="hgi-stroke ${ic.name}"></i>
            <span class="icon-picker-item-label">${ic.label}</span>
        </div>`;
    }).join('');

    grid.querySelectorAll('.icon-picker-item').forEach(item => {
        item.addEventListener('click', () => {
            const iconName = (item as HTMLElement).dataset.icon!;
            selectIcon(iconName);
        });
    });
}

function selectIcon(iconName: string): void {
    if (iconPickerTargetIdx >= 0 && customButtons[iconPickerTargetIdx]) {
        customButtons[iconPickerTargetIdx].icon = iconName;
        renderCustomButtons();
    }
    closeIconPicker();
}

function closeIconPicker(): void {
    document.getElementById('icon-picker-modal')!.style.display = 'none';
    iconPickerTargetIdx = -1;
}

// -------------------------------------------------------------------
// Custom Buttons Editor
// -------------------------------------------------------------------

let customButtons: CustomButton[] = [];

function renderCustomButtons(): void {
    const container = document.getElementById('custom-buttons-list');
    if (!container) return;

    container.innerHTML = '';

    customButtons.forEach((btn, index) => {
        const entry = document.createElement('div');
        entry.className = 'custom-btn-entry';
        entry.innerHTML = `
            <div class="custom-btn-icon-col">
                <label>Icon</label>
                <button type="button" class="custom-btn-icon-preview" data-idx="${index}" title="Choose icon">
                    <i class="hgi-stroke ${escapeHtml(btn.icon || 'hgi-eye')}"></i>
                </button>
            </div>
            <div>
                <label>Button Label</label>
                <input type="text" data-idx="${index}" data-field="label" value="${escapeHtml(btn.label)}" placeholder="e.g. Show Interior">
            </div>
            <div>
                <label>Action</label>
                <select data-idx="${index}" data-field="action">
                    <option value="toggle" ${btn.action === 'toggle' ? 'selected' : ''}>Toggle</option>
                    <option value="hide" ${btn.action === 'hide' ? 'selected' : ''}>Hide</option>
                    <option value="show" ${btn.action === 'show' ? 'selected' : ''}>Show</option>
                </select>
            </div>
            <div>
                <label>Mesh Names (comma-separated or regex)</label>
                <div style="display:flex;gap:6px;align-items:center;">
                    <input type="text" data-idx="${index}" data-field="meshPattern" value="${escapeHtml(btn.meshPattern)}" placeholder="e.g. Ceilings1,Ceilings2 or ^Roof.*" style="flex:1;">
                    <button type="button" class="custom-btn-insert-meshes" data-idx="${index}" title="Insert selected mesh names from 3D preview">Insert</button>
                </div>
            </div>
            <button type="button" class="custom-btn-remove" data-idx="${index}" title="Remove">
                <i class="hgi hgi-delete-02"></i>
            </button>
        `;
        container.appendChild(entry);
    });

    // Bind input change events
    container.querySelectorAll('input, select').forEach((el) => {
        el.addEventListener('change', () => {
            const idx = parseInt((el as HTMLElement).dataset.idx || '0');
            const field = (el as HTMLElement).dataset.field as keyof CustomButton;
            if (customButtons[idx] && field) {
                (customButtons[idx] as any)[field] = (el as HTMLInputElement).value;
            }
        });
        // Also listen for input (for text fields)
        if (el instanceof HTMLInputElement) {
            el.addEventListener('input', () => {
                const idx = parseInt(el.dataset.idx || '0');
                const field = el.dataset.field as keyof CustomButton;
                if (customButtons[idx] && field) {
                    (customButtons[idx] as any)[field] = el.value;
                }
            });
        }
    });

    // Bind remove buttons
    container.querySelectorAll('.custom-btn-remove').forEach((btn) => {
        btn.addEventListener('click', () => {
            const idx = parseInt((btn as HTMLElement).dataset.idx || '0');
            customButtons.splice(idx, 1);
            renderCustomButtons();
        });
    });

    // Bind insert meshes buttons
    container.querySelectorAll('.custom-btn-insert-meshes').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const idx = parseInt((btn as HTMLElement).dataset.idx || '0');
            insertMeshesToPattern(idx);
        });
    });

    // Bind icon picker buttons
    container.querySelectorAll('.custom-btn-icon-preview').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const idx = parseInt((btn as HTMLElement).dataset.idx || '0');
            openIconPicker(idx);
        });
    });
}

function setupCustomButtonsEditor(): void {
    document.getElementById('add-custom-btn-btn')?.addEventListener('click', () => {
        customButtons.push({
            label: '',
            icon: 'hgi-eye',
            meshPattern: '',
            action: 'toggle',
        });
        renderCustomButtons();
    });
}

function collectCustomButtons(): CustomButton[] {
    return customButtons.filter(b => b.label.trim() && b.meshPattern.trim());
}

async function openEditor(property: Property | null): Promise<void> {
    editingPropertyId = property?.id || null;

    // Update title
    const titleEl = document.getElementById('editor-title')!;
    const deleteBtn = document.getElementById('editor-delete-btn')!;

    if (property) {
        titleEl.textContent = `Edit: ${property.title}`;
        deleteBtn.style.display = 'inline-flex';
    } else {
        titleEl.textContent = 'Add Property';
        deleteBtn.style.display = 'none';
    }

    // Populate form fields
    (document.getElementById('prop-id') as HTMLInputElement).value = property?.id || '';
    (document.getElementById('prop-title') as HTMLInputElement).value = property?.title || '';
    (document.getElementById('prop-subtitle') as HTMLInputElement).value = property?.subtitle || '';
    (document.getElementById('prop-slug') as HTMLInputElement).value = property?.slug || '';
    // subdomain field removed — routing is via path slug now
    (document.getElementById('prop-description') as HTMLTextAreaElement).value = property?.description || '';
    (document.getElementById('prop-thumbnail') as HTMLInputElement).value = property?.thumbnail_url || '';
    (document.getElementById('prop-active') as HTMLInputElement).checked = property?.is_active ?? true;
    (document.getElementById('prop-glb') as HTMLInputElement).value = property?.glb_model_url || '';
    (document.getElementById('prop-email') as HTMLInputElement).value = property?.form_email || '';
    (document.getElementById('prop-subject') as HTMLInputElement).value = property?.form_subject_prefix || '';
    (document.getElementById('prop-button') as HTMLInputElement).value = property?.button_title || 'INTERESTED?';
    // Populate HDR checklist and dropdown from database
    hdrList = await fetchAllHDRs();
    populateHDRChecklist(property?.active_hdrs || []);
    populateHDRDropdown(property?.default_hdr || 'Golden Gate Hills');

    // Populate render settings
    const rs = { ...DEFAULT_RENDER_SETTINGS, ...(property?.render_settings || {}) };
    populateRenderSettings(rs);

    // Populate custom buttons
    customButtons = property?.custom_buttons ? [...property.custom_buttons] : [];
    renderCustomButtons();

    showView('editor');
}

function populateRenderSettings(rs: RenderSettings): void {
    RENDER_SETTING_KEYS.forEach((key) => {
        const el = document.getElementById(`rs-${key}`);
        if (!el) return;

        const val = rs[key];

        if (el instanceof HTMLSelectElement) {
            el.value = String(val);
        } else if (el instanceof HTMLInputElement) {
            if (el.type === 'checkbox') {
                el.checked = Boolean(val);
            } else if (el.type === 'color') {
                el.value = String(val);
            } else if (el.type === 'range' || el.type === 'number') {
                el.value = String(val);
                // Update display value for range sliders
                const display = document.getElementById(`rs-${key}-val`);
                if (display) {
                    display.textContent = formatRangeValue(key, Number(val));
                }
            }
        }
    });
}

function formatRangeValue(key: string, val: number): string {
    if (key === 'fogDensity') return val.toFixed(4);
    if (key === 'exposure') return val.toFixed(1);
    return val.toFixed(2);
}

function collectRenderSettings(): RenderSettings {
    const rs: any = { ...DEFAULT_RENDER_SETTINGS };

    RENDER_SETTING_KEYS.forEach((key) => {
        const el = document.getElementById(`rs-${key}`);
        if (!el) return;

        if (el instanceof HTMLSelectElement) {
            rs[key] = el.value;
        } else if (el instanceof HTMLInputElement) {
            if (el.type === 'checkbox') {
                rs[key] = el.checked;
            } else if (el.type === 'color') {
                rs[key] = el.value;
            } else if (el.type === 'range' || el.type === 'number') {
                rs[key] = parseFloat(el.value);
            }
        }
    });

    return rs as RenderSettings;
}

function collectPropertyData(): Partial<Property> {
    return {
        title: (document.getElementById('prop-title') as HTMLInputElement).value.trim(),
        subtitle: (document.getElementById('prop-subtitle') as HTMLInputElement).value.trim(),
        slug: (document.getElementById('prop-slug') as HTMLInputElement).value.trim(),
        description: (document.getElementById('prop-description') as HTMLTextAreaElement).value.trim(),
        thumbnail_url: (document.getElementById('prop-thumbnail') as HTMLInputElement).value.trim(),
        is_active: (document.getElementById('prop-active') as HTMLInputElement).checked,
        glb_model_url: (document.getElementById('prop-glb') as HTMLInputElement).value.trim(),
        form_email: (document.getElementById('prop-email') as HTMLInputElement).value.trim(),
        form_subject_prefix: (document.getElementById('prop-subject') as HTMLInputElement).value.trim(),
        button_title: (document.getElementById('prop-button') as HTMLInputElement).value.trim(),
        default_hdr: (document.getElementById('prop-hdr') as HTMLSelectElement).value,
        active_hdrs: getCheckedHDRNames(),
        render_settings: collectRenderSettings(),
        custom_buttons: collectCustomButtons(),
    };
}

function setupEditorEvents(): void {
    // HDR upload + browse handlers
    setupHDRUpload();
    setupCloudinaryBrowse();

    // Icon picker
    setupIconPicker();

    // Custom buttons editor
    setupCustomButtonsEditor();

    // 3D model preview
    setupModelPreview();

    // Auto-generate slug from title
    const titleInput = document.getElementById('prop-title') as HTMLInputElement;
    const slugInput = document.getElementById('prop-slug') as HTMLInputElement;

    titleInput?.addEventListener('input', () => {
        // Only auto-generate if creating new property (not editing)
        if (!editingPropertyId) {
            slugInput.value = generateSlug(titleInput.value);
        }
    });

    // Range slider live updates
    RENDER_SETTING_KEYS.forEach((key) => {
        const el = document.getElementById(`rs-${key}`);
        if (el instanceof HTMLInputElement && el.type === 'range') {
            el.addEventListener('input', () => {
                const display = document.getElementById(`rs-${key}-val`);
                if (display) {
                    display.textContent = formatRangeValue(key, parseFloat(el.value));
                }
            });
        }
    });

    // Save button
    document.getElementById('editor-save-btn')?.addEventListener('click', async () => {
        const data = collectPropertyData();

        // Validate
        if (!data.title || !data.slug || !data.glb_model_url || !data.form_email) {
            showToast('Please fill in all required fields', true);
            return;
        }

        try {
            const saveBtn = document.getElementById('editor-save-btn')!;
            saveBtn.innerHTML = '<i class="hgi hgi-loading-01"></i> <span>Saving...</span>';
            (saveBtn as HTMLButtonElement).disabled = true;

            if (editingPropertyId) {
                await updateProperty(editingPropertyId, data);
                showToast('Property updated successfully');
            } else {
                await createProperty(data as any);
                showToast('Property created successfully');
            }

            // Dispose preview and navigate back to overview
            disposeModelPreview();
            setTimeout(() => navigate('overview'), 500);
        } catch (err: any) {
            console.error('Save failed:', err);
            showToast(err.message || 'Failed to save property', true);
        } finally {
            const saveBtn = document.getElementById('editor-save-btn')!;
            saveBtn.innerHTML = '<i class="hgi hgi-checkmark-circle-02"></i> <span>Save Property</span>';
            (saveBtn as HTMLButtonElement).disabled = false;
        }
    });

    // Cancel button
    document.getElementById('editor-cancel-btn')?.addEventListener('click', () => {
        disposeModelPreview();
        navigate('overview');
    });

    // Delete button
    document.getElementById('editor-delete-btn')?.addEventListener('click', () => {
        const modal = document.getElementById('delete-modal')!;
        modal.style.display = 'flex';
    });

    // Delete confirm
    document.getElementById('delete-confirm-btn')?.addEventListener('click', async () => {
        if (!editingPropertyId) return;

        try {
            await deleteProperty(editingPropertyId);
            document.getElementById('delete-modal')!.style.display = 'none';
            showToast('Property deleted');
            navigate('overview');
        } catch (err) {
            console.error('Delete failed:', err);
            showToast('Failed to delete property', true);
        }
    });

    // Delete cancel
    document.getElementById('delete-cancel-btn')?.addEventListener('click', () => {
        document.getElementById('delete-modal')!.style.display = 'none';
    });
}

// -------------------------------------------------------------------
// 3D Model Preview — Mesh Picker
// -------------------------------------------------------------------

let previewRenderer: THREE.WebGLRenderer | null = null;
let previewScene: THREE.Scene | null = null;
let previewCamera: THREE.PerspectiveCamera | null = null;
let previewControls: OrbitControls | null = null;
let previewModel: THREE.Group | null = null;
let previewAnimId = 0;
let previewMeshNames: string[] = [];
let selectedMeshNames = new Set<string>();
let highlightedMeshName: string | null = null;
const originalMaterials = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();

function initModelPreview(): void {
    const glbUrl = (document.getElementById('prop-glb') as HTMLInputElement)?.value.trim();
    if (!glbUrl) {
        showToast('Enter a GLB Model URL first', true);
        return;
    }

    const container = document.getElementById('model-preview-container')!;
    const status = document.getElementById('model-preview-status')!;
    const canvas = document.getElementById('model-preview-canvas') as HTMLCanvasElement;
    const loadBtn = document.getElementById('load-model-preview-btn')!;

    // If already loaded, dispose first
    if (previewRenderer) {
        disposeModelPreview();
    }

    // Show container
    container.style.display = 'block';
    status.classList.remove('hidden');
    status.textContent = 'Loading model...';
    loadBtn.innerHTML = '<i class="hgi hgi-loading-01"></i> Loading...';
    (loadBtn as HTMLButtonElement).disabled = true;

    // Setup renderer
    previewRenderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: false,
    });
    previewRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    previewRenderer.setClearColor(0x1a1a2e);
    previewRenderer.toneMapping = THREE.ACESFilmicToneMapping;
    previewRenderer.toneMappingExposure = 1.2;

    // Scene
    previewScene = new THREE.Scene();
    previewScene.background = new THREE.Color(0x1a1a2e);

    // Camera
    const wrap = canvas.parentElement!;
    const w = wrap.clientWidth;
    const h = wrap.clientHeight || 400;
    previewCamera = new THREE.PerspectiveCamera(45, w / h, 0.1, 2000);
    previewCamera.position.set(30, 20, 30);
    previewRenderer.setSize(w, h);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    previewScene.add(ambient);

    const directional = new THREE.DirectionalLight(0xffffff, 1.0);
    directional.position.set(50, 80, 50);
    previewScene.add(directional);

    const hemi = new THREE.HemisphereLight(0xb1e1ff, 0xb97a20, 0.4);
    previewScene.add(hemi);

    // Controls
    previewControls = new OrbitControls(previewCamera, canvas);
    previewControls.enableDamping = true;
    previewControls.dampingFactor = 0.1;
    previewControls.enablePan = true;
    previewControls.minDistance = 2;
    previewControls.maxDistance = 500;

    // Load Model
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
        glbUrl + '?v=' + Date.now(),
        (gltf) => {
            previewModel = gltf.scene;

            // Center and fit model
            const box = new THREE.Box3().setFromObject(previewModel);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);
            const scale = 20 / maxDim;
            previewModel.scale.setScalar(scale);
            previewModel.position.sub(center.multiplyScalar(scale));

            previewScene!.add(previewModel);

            // Position camera based on model size
            const dist = 40;
            previewCamera!.position.set(dist * 0.7, dist * 0.5, dist * 0.7);
            previewControls!.target.set(0, 0, 0);
            previewControls!.update();

            // Extract mesh names
            previewMeshNames = extractMeshNames(previewModel);
            selectedMeshNames.clear();
            highlightedMeshName = null;
            originalMaterials.clear();

            // Populate mesh list
            renderMeshList();

            // Update UI
            status.classList.add('hidden');
            loadBtn.innerHTML = '<i class="hgi hgi-reload-02"></i> Reload';
            (loadBtn as HTMLButtonElement).disabled = false;

            console.log(`[Preview] Loaded model with ${previewMeshNames.length} meshes`);
        },
        (progress) => {
            if (progress.total > 0) {
                const pct = Math.round((progress.loaded / progress.total) * 100);
                status.textContent = `Loading model... ${pct}%`;
            }
        },
        (error) => {
            console.error('[Preview] Failed to load model:', error);
            status.textContent = 'Failed to load model';
            status.classList.remove('hidden');
            loadBtn.innerHTML = '<i class="hgi hgi-play-circle"></i> Retry';
            (loadBtn as HTMLButtonElement).disabled = false;
        },
    );

    // Setup raycasting for click-to-select
    canvas.addEventListener('click', onPreviewCanvasClick);

    // Start render loop
    function animate() {
        previewAnimId = requestAnimationFrame(animate);
        if (previewControls) previewControls.update();
        if (previewRenderer && previewScene && previewCamera) {
            previewRenderer.render(previewScene, previewCamera);
        }
    }
    animate();

    // Resize observer
    const resizeObserver = new ResizeObserver(() => {
        if (!previewRenderer || !previewCamera) return;
        const rw = wrap.clientWidth;
        const rh = wrap.clientHeight || 400;
        previewCamera.aspect = rw / rh;
        previewCamera.updateProjectionMatrix();
        previewRenderer.setSize(rw, rh);
    });
    resizeObserver.observe(wrap);

    // Store observer for cleanup
    (canvas as any).__resizeObserver = resizeObserver;
}

function extractMeshNames(root: THREE.Object3D): string[] {
    const names: string[] = [];
    root.traverse((child) => {
        if ((child as THREE.Mesh).isMesh && child.name) {
            names.push(child.name);
        }
    });
    return names.sort((a, b) => a.localeCompare(b));
}

function renderMeshList(filter?: string): void {
    const container = document.getElementById('mesh-list');
    const countEl = document.getElementById('mesh-count');
    if (!container) return;

    const lowerFilter = filter?.toLowerCase() || '';
    const filtered = lowerFilter
        ? previewMeshNames.filter(n => n.toLowerCase().includes(lowerFilter))
        : previewMeshNames;

    if (countEl) {
        countEl.textContent = `${filtered.length} mesh${filtered.length !== 1 ? 'es' : ''}`;
    }

    container.innerHTML = filtered.map(name => {
        const checked = selectedMeshNames.has(name) ? 'checked' : '';
        const highlighted = highlightedMeshName === name ? ' highlighted' : '';
        const selected = selectedMeshNames.has(name) ? ' selected' : '';
        return `<div class="mesh-item${highlighted}${selected}" data-name="${escapeHtml(name)}">
            <input type="checkbox" ${checked}>
            <span>${escapeHtml(name)}</span>
        </div>`;
    }).join('');

    // Bind click events
    container.querySelectorAll('.mesh-item').forEach(item => {
        item.addEventListener('click', (e) => {
            const name = (item as HTMLElement).dataset.name || '';
            const checkbox = item.querySelector('input[type="checkbox"]') as HTMLInputElement;

            // Toggle selection
            if (selectedMeshNames.has(name)) {
                selectedMeshNames.delete(name);
                checkbox.checked = false;
                item.classList.remove('selected');
            } else {
                selectedMeshNames.add(name);
                checkbox.checked = true;
                item.classList.add('selected');
            }

            // Highlight in 3D preview
            highlightMeshInPreview(name);
            e.preventDefault();
        });
    });
}

function highlightMeshInPreview(name: string | null): void {
    if (!previewModel) return;

    // Restore previous highlight
    if (highlightedMeshName) {
        previewModel.traverse((child) => {
            if ((child as THREE.Mesh).isMesh && child.name === highlightedMeshName) {
                const mesh = child as THREE.Mesh;
                const orig = originalMaterials.get(mesh);
                if (orig) {
                    mesh.material = orig as THREE.Material;
                    originalMaterials.delete(mesh);
                }
            }
        });
    }

    highlightedMeshName = name;

    // Apply new highlight
    if (name) {
        previewModel.traverse((child) => {
            if ((child as THREE.Mesh).isMesh && child.name === name) {
                const mesh = child as THREE.Mesh;
                // Store original
                if (!originalMaterials.has(mesh)) {
                    originalMaterials.set(mesh, mesh.material);
                }
                // Clone material and add emissive highlight
                const mat = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial;
                const highlighted = mat.clone();
                highlighted.emissive = new THREE.Color(0x00B4D8);
                highlighted.emissiveIntensity = 0.4;
                highlighted.wireframe = false;
                mesh.material = highlighted;
            }
        });

        // Update list item highlighting
        document.querySelectorAll('#mesh-list .mesh-item').forEach(item => {
            item.classList.toggle('highlighted', (item as HTMLElement).dataset.name === name);
        });
    }
}

function onPreviewCanvasClick(event: MouseEvent): void {
    if (!previewRenderer || !previewCamera || !previewScene) return;

    const canvas = previewRenderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, previewCamera);

    const intersects = raycaster.intersectObjects(previewScene.children, true);
    for (const hit of intersects) {
        const obj = hit.object;
        if ((obj as THREE.Mesh).isMesh && obj.name) {
            // Toggle selection
            if (selectedMeshNames.has(obj.name)) {
                selectedMeshNames.delete(obj.name);
            } else {
                selectedMeshNames.add(obj.name);
            }

            // Highlight
            highlightMeshInPreview(obj.name);

            // Update checkbox in list
            const searchInput = document.getElementById('mesh-search') as HTMLInputElement;
            renderMeshList(searchInput?.value || '');
            return;
        }
    }
}

function getSelectedMeshNamesCSV(): string {
    return Array.from(selectedMeshNames).join(', ');
}

function insertMeshesToPattern(index: number): void {
    const csv = getSelectedMeshNamesCSV();
    if (!csv) {
        showToast('Select meshes from the 3D preview first', true);
        return;
    }

    if (customButtons[index]) {
        customButtons[index].meshPattern = csv;
        renderCustomButtons();
        showToast(`Inserted ${selectedMeshNames.size} mesh name(s)`);
    }
}

function disposeModelPreview(): void {
    // Cancel animation
    if (previewAnimId) {
        cancelAnimationFrame(previewAnimId);
        previewAnimId = 0;
    }

    // Remove click handler
    const canvas = document.getElementById('model-preview-canvas') as HTMLCanvasElement;
    if (canvas) {
        canvas.removeEventListener('click', onPreviewCanvasClick);
        // Remove resize observer
        const obs = (canvas as any).__resizeObserver;
        if (obs) {
            obs.disconnect();
            (canvas as any).__resizeObserver = null;
        }
    }

    // Restore original materials
    originalMaterials.forEach((mat, mesh) => {
        mesh.material = mat as THREE.Material;
    });
    originalMaterials.clear();

    // Dispose model
    if (previewModel) {
        previewModel.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                mesh.geometry?.dispose();
                if (Array.isArray(mesh.material)) {
                    mesh.material.forEach(m => m.dispose());
                } else {
                    mesh.material?.dispose();
                }
            }
        });
        previewScene?.remove(previewModel);
        previewModel = null;
    }

    // Dispose controls
    previewControls?.dispose();
    previewControls = null;

    // Dispose renderer
    previewRenderer?.dispose();
    previewRenderer = null;

    previewScene = null;
    previewCamera = null;
    previewMeshNames = [];
    selectedMeshNames.clear();
    highlightedMeshName = null;

    // Hide container
    const container = document.getElementById('model-preview-container');
    if (container) container.style.display = 'none';

    // Reset button
    const loadBtn = document.getElementById('load-model-preview-btn');
    if (loadBtn) {
        loadBtn.innerHTML = '<i class="hgi hgi-play-circle"></i> Load Preview';
        (loadBtn as HTMLButtonElement).disabled = false;
    }
}

function setupModelPreview(): void {
    // Load Preview button
    document.getElementById('load-model-preview-btn')?.addEventListener('click', () => {
        initModelPreview();
    });

    // Search filter
    document.getElementById('mesh-search')?.addEventListener('input', (e) => {
        const val = (e.target as HTMLInputElement).value;
        renderMeshList(val);
    });

    // Select All / Deselect All
    document.getElementById('mesh-select-all')?.addEventListener('click', () => {
        const searchVal = (document.getElementById('mesh-search') as HTMLInputElement)?.value.toLowerCase() || '';
        const filtered = searchVal
            ? previewMeshNames.filter(n => n.toLowerCase().includes(searchVal))
            : previewMeshNames;
        filtered.forEach(n => selectedMeshNames.add(n));
        renderMeshList(searchVal);
    });

    document.getElementById('mesh-deselect-all')?.addEventListener('click', () => {
        selectedMeshNames.clear();
        highlightMeshInPreview(null);
        const searchVal = (document.getElementById('mesh-search') as HTMLInputElement)?.value || '';
        renderMeshList(searchVal);
    });
}

// -------------------------------------------------------------------
// Toast Notification
// -------------------------------------------------------------------

function showToast(message: string, isError: boolean = false): void {
    const toast = document.getElementById('toast')!;
    toast.textContent = message;
    toast.className = 'toast' + (isError ? ' error' : '');

    // Trigger show
    requestAnimationFrame(() => {
        toast.classList.add('show');
    });

    // Hide after 3s
    setTimeout(() => {
        toast.classList.remove('show');
    }, 3000);
}

// -------------------------------------------------------------------
// Utilities
// -------------------------------------------------------------------

function escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

function showEmpty(canvasId: string, message: string): void {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;
    const parent = canvas.parentElement!;
    canvas.style.display = 'none';
    const existing = parent.querySelector('.empty-state');
    if (existing) existing.remove();
    parent.insertAdjacentHTML('beforeend', `
        <div class="empty-state">
            <i class="hgi hgi-chart-line-data-01"></i>
            <span>${message}</span>
        </div>
    `);
}

// -------------------------------------------------------------------
// Boot
// -------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', initAuth);
